import {
    CommandLineIcon,
    PhoneArrowUpRightIcon,
    CpuChipIcon,
    Squares2X2Icon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";
import type { FaqItem } from "../components/seo/FAQSection";

/**
 * Competitor comparison ("FloGPT vs X") + alternatives content surfaced at
 * `/compare`, `/compare/:slug`, and `/alternatives`.
 *
 * Editorial rules (these pages name real competitors, so accuracy matters):
 *   - Describe each competitor fairly and at a high level. Capability cells
 *     are framed around product FOCUS, not hard feature claims we can't keep
 *     current. Every page renders COMPARISON_DISCLAIMER.
 *   - Include an honest "when {competitor} is the better fit" block — balanced
 *     comparisons earn more trust and are cited more often by AI engines.
 *   - Keep FAQ answers 40–80 words, customer-phrased, brand-self-contained.
 */

export const COMPARISON_DISCLAIMER =
    "Comparison based on publicly available information as of June 2026. Competitor products evolve quickly — verify current capabilities on their official site. All trademarks belong to their respective owners.";

export type CellTone = "yes" | "partial" | "no";

export interface ComparisonCell {
    tone: CellTone;
    text: string;
}

export interface ComparisonRow {
    capability: string;
    flogpt: ComparisonCell;
    competitor: ComparisonCell;
}

export interface ComparisonItem {
    /** URL slug, e.g. "flogpt-vs-vapi". */
    slug: string;
    /** Competitor display name, e.g. "Vapi". */
    competitor: string;
    /** Competitor primary domain (shown as a mono label). */
    competitorDomain: string;
    /** Grouping key for the /alternatives hub. */
    category: ComparisonCategoryKey;
    Icon: ComponentType<{ className?: string }>;
    /** Tailwind gradient stops, e.g. "from-orange-500 to-pink-500". */
    accent: string;
    /** Tailwind soft-glow class, e.g. "bg-orange-500/15". */
    softAccent: string;
    /** Compact card tagline (1 sentence). */
    tagline: string;
    /** Answer-first hero summary (2–3 sentences) — quoted by AI overviews. */
    heroSummary: string;
    /** Fair, high-level description of what the competitor is/does. */
    competitorSummary: string;
    /** Capability comparison rows. */
    rows: ComparisonRow[];
    /** Why teams pick FloGPT — 3 tailored reasons. */
    whyFloGPT: Array<{ title: string; description: string }>;
    /** Honest "choose the competitor when…" guidance. */
    competitorBestFor: string;
    /** How a team typically moves from the competitor to FloGPT. */
    migrationNote: string;
    faqs: FaqItem[];
    seo: {
        title: string;
        description: string;
        keywords: string[];
    };
}

export type ComparisonCategoryKey = "voice";

export interface ComparisonCategory {
    key: ComparisonCategoryKey;
    /** Hub-page label, e.g. "AI voice agent platforms". */
    label: string;
    /** Short blurb under the category heading on /alternatives. */
    blurb: string;
}

export const COMPARISON_CATEGORIES: ComparisonCategory[] = [
    {
        key: "voice",
        label: "AI voice agent platforms",
        blurb:
            "Voice-first tools focus on building phone agents. FloGPT runs the same AI across voice, web chat, and seven messaging channels — with a built-in CRM inbox, knowledge base, and outbound engine in one product.",
    },
];

const FLOGPT_CELLS = {
    voiceAgents: { tone: "yes", text: "Inbound + outbound, 16 languages" } as ComparisonCell,
    chatWidget: { tone: "yes", text: "Embeddable widget for any website" } as ComparisonCell,
    crmInbox: { tone: "yes", text: "Chat, voice, email, SMS, WhatsApp, IG, FB" } as ComparisonCell,
    knowledgeBase: { tone: "yes", text: "Managed RAG with inline citations" } as ComparisonCell,
    identity: { tone: "yes", text: "Cross-channel contact merge" } as ComparisonCell,
    handover: { tone: "yes", text: "Tickets, SLA, role-scoped analytics" } as ComparisonCell,
    builders: { tone: "yes", text: "Visual workflow + call pipeline editors" } as ComparisonCell,
    outbound: { tone: "yes", text: "Webhooks, Shopify, time-relative, lists" } as ComparisonCell,
    verticals: { tone: "yes", text: "13 industry packs, prompts pre-tuned" } as ComparisonCell,
    indic: { tone: "yes", text: "9 Indic locales + Hindi, DLT-ready" } as ComparisonCell,
    freeTier: { tone: "yes", text: "Free chat tier; paid plans add voice" } as ComparisonCell,
};

/**
 * Shared capability matrix. The FloGPT column is constant; the competitor
 * column is characterised per product focus so the cells stay fair.
 */
const voiceComparisonRows = (c: {
    voiceAgents: ComparisonCell;
    chatWidget: ComparisonCell;
    crmInbox: ComparisonCell;
    knowledgeBase: ComparisonCell;
    identity: ComparisonCell;
    handover: ComparisonCell;
    builders: ComparisonCell;
    outbound: ComparisonCell;
    verticals: ComparisonCell;
    indic: ComparisonCell;
    pricing: ComparisonCell;
}): ComparisonRow[] => [
    { capability: "AI voice agents (inbound + outbound)", flogpt: FLOGPT_CELLS.voiceAgents, competitor: c.voiceAgents },
    { capability: "AI chat widget for websites", flogpt: FLOGPT_CELLS.chatWidget, competitor: c.chatWidget },
    { capability: "Unified omnichannel CRM inbox", flogpt: FLOGPT_CELLS.crmInbox, competitor: c.crmInbox },
    { capability: "Built-in knowledge base (RAG) with citations", flogpt: FLOGPT_CELLS.knowledgeBase, competitor: c.knowledgeBase },
    { capability: "Cross-channel contact identity resolution", flogpt: FLOGPT_CELLS.identity, competitor: c.identity },
    { capability: "AI→human handover, tickets & analytics", flogpt: FLOGPT_CELLS.handover, competitor: c.handover },
    { capability: "No-code workflow & call-flow builders", flogpt: FLOGPT_CELLS.builders, competitor: c.builders },
    { capability: "Outbound trigger engine", flogpt: FLOGPT_CELLS.outbound, competitor: c.outbound },
    { capability: "Pre-built vertical templates", flogpt: FLOGPT_CELLS.verticals, competitor: c.verticals },
    { capability: "Indic-language depth (Hindi + 9 locales)", flogpt: FLOGPT_CELLS.indic, competitor: c.indic },
    { capability: "Pricing model", flogpt: FLOGPT_CELLS.freeTier, competitor: c.pricing },
];

export const COMPARISONS: ComparisonItem[] = [
    {
        slug: "flogpt-vs-vapi",
        competitor: "Vapi",
        competitorDomain: "vapi.ai",
        category: "voice",
        Icon: CommandLineIcon,
        accent: "from-emerald-500 to-teal-500",
        softAccent: "bg-emerald-500/15",
        tagline: "Developer voice API vs an all-in-one chat, voice & CRM platform.",
        heroSummary:
            "Vapi is a developer-first voice AI platform: you assemble phone agents from APIs and SDKs, bringing your own models, prompts, and integrations. FloGPT is an all-in-one AI customer platform — voice agents plus a web chat widget, a unified CRM inbox, a managed knowledge base, and an outbound engine — that a non-engineer can ship in an afternoon.",
        competitorSummary:
            "Vapi gives developers low-level building blocks (speech-to-text, LLM, text-to-speech orchestration) to build and scale voice agents programmatically. It's a strong fit when you want full control in code and are happy to own the surrounding stack.",
        rows: voiceComparisonRows({
            voiceAgents: { tone: "yes", text: "Core product (API/SDK)" },
            chatWidget: { tone: "partial", text: "Chat API; you add the chat front end" },
            crmInbox: { tone: "no", text: "No built-in inbox" },
            knowledgeBase: { tone: "partial", text: "Bring your own RAG/store" },
            identity: { tone: "no", text: "Handle in your own backend" },
            handover: { tone: "partial", text: "Transfer primitives; no ticketing" },
            builders: { tone: "partial", text: "Dashboard + code-first config" },
            outbound: { tone: "partial", text: "Outbound API; you build triggers" },
            verticals: { tone: "no", text: "No vertical templates" },
            indic: { tone: "partial", text: "Many languages; depends on chosen models" },
            pricing: { tone: "partial", text: "Per-minute usage + model/telephony costs" },
        }),
        whyFloGPT: [
            { title: "One platform, not a toolkit", description: "Voice, chat, knowledge base, and a CRM inbox ship together — no glue code, no separate vendors for STT/LLM/TTS, storage, and ticketing." },
            { title: "Non-engineers can ship", description: "Visual call-pipeline and workflow editors, vertical templates, and a crawl-your-site knowledge base mean a support lead can launch without a developer." },
            { title: "Conversations become tickets", description: "Every call and chat lands in a unified inbox with contact identity, SLA, and analytics — so voice isn't a silo from the rest of support." },
        ],
        competitorBestFor:
            "Choose Vapi if you have an engineering team that wants low-level, code-first control over a voice pipeline and intends to build the surrounding product (UI, storage, CRM) yourself.",
        migrationNote:
            "Teams usually move to FloGPT when the DIY surface area around Vapi (knowledge base, inbox, analytics, channels beyond voice) becomes more work than the agent itself. You keep your phone numbers and rebuild the call flow visually in the Call Pipeline editor.",
        faqs: [
            { question: "Is FloGPT a good Vapi alternative?", answer: "Yes, if you want a finished product rather than building blocks. Vapi is a developer voice API; FloGPT ships voice agents, a website chat widget, a managed knowledge base, and a unified CRM inbox together, so non-engineers can launch and operate AI support without assembling the stack themselves." },
            { question: "What does FloGPT include that Vapi doesn't?", answer: "FloGPT ships an embeddable chat widget, a seven-channel CRM inbox, contact identity, a managed knowledge base with citations, ticketing, and vertical templates. Vapi's Chat API still needs a chat front end, a CRM inbox, and your own knowledge store." },
            { question: "Do I need developers to use FloGPT?", answer: "No. FloGPT is designed for non-engineers: point the crawler at your website to build the knowledge base, pick a vertical template, and design call flows in a visual editor. Developers can still extend it with HTTP nodes, custom tools, and the API when needed." },
            { question: "Can FloGPT place outbound calls like Vapi?", answer: "Yes. FloGPT runs inbound and outbound voice, and adds a trigger engine that fires outbound calls from webhooks, Shopify events, scheduled lists, and time-relative cadences — with quiet hours, dedup, and retry policy built in, rather than something you orchestrate in code." },
        ],
        seo: {
            title: "FloGPT vs Vapi — AI Voice & Chat Platform Comparison",
            description: "FloGPT vs Vapi compared: Vapi is a developer voice API; FloGPT is an all-in-one AI chat, voice, and CRM platform you can ship without engineers. See the capability comparison.",
            keywords: ["FloGPT vs Vapi", "Vapi alternative", "Vapi alternatives", "AI voice agent platform", "Vapi competitor", "voice AI API alternative"],
        },
    },
    {
        slug: "flogpt-vs-bland",
        competitor: "Bland",
        competitorDomain: "bland.ai",
        category: "voice",
        Icon: PhoneArrowUpRightIcon,
        accent: "from-orange-500 to-pink-500",
        softAccent: "bg-orange-500/15",
        tagline: "High-volume AI phone calling vs an omnichannel AI support platform.",
        heroSummary:
            "Bland is an AI phone-calling platform focused on automating inbound and outbound calls at scale through an API. FloGPT covers phone calls too, but pairs them with a website chat widget, a unified CRM inbox across seven channels, and a managed knowledge base — so support, sales, and outreach live in one product instead of voice alone.",
        competitorSummary:
            "Bland specialises in programmatic phone calls — sending and receiving AI voice calls at volume with low-latency speech. It's a good fit for teams whose primary need is phone automation and who will integrate it into their own systems.",
        rows: voiceComparisonRows({
            voiceAgents: { tone: "yes", text: "Core product (phone calls at scale)" },
            chatWidget: { tone: "no", text: "Phone only" },
            crmInbox: { tone: "no", text: "No built-in inbox" },
            knowledgeBase: { tone: "partial", text: "Prompt/context you supply" },
            identity: { tone: "no", text: "Manage in your own systems" },
            handover: { tone: "partial", text: "Call transfer; no ticketing" },
            builders: { tone: "partial", text: "Conversational pathways + API" },
            outbound: { tone: "yes", text: "Strong outbound calling" },
            verticals: { tone: "no", text: "No vertical templates" },
            indic: { tone: "partial", text: "Multilingual; Indic depth varies" },
            pricing: { tone: "partial", text: "Per-minute usage pricing" },
        }),
        whyFloGPT: [
            { title: "More than the phone", description: "Bland is excellent at calls; FloGPT answers calls and chats, then unifies email, SMS, WhatsApp, Instagram, and Facebook into the same inbox so customers aren't fragmented by channel." },
            { title: "Grounded answers out of the box", description: "FloGPT's managed knowledge base crawls your site and documents and grounds every reply with citations — no separate retrieval stack to build and maintain." },
            { title: "Operational, not just conversational", description: "Tickets, SLA timers, contact identity, and role-scoped analytics turn AI conversations into something your team can actually run a support org on." },
        ],
        competitorBestFor:
            "Choose Bland if phone calling at scale is essentially your whole use case and you plan to wire it into your own CRM, dialer, and reporting.",
        migrationNote:
            "Teams switch to FloGPT when they need chat, messaging, and a real inbox alongside calls. Your numbers and outbound use cases map onto FloGPT's Call Pipeline and Outbound Triggers, with quiet hours and dedup handled for you.",
        faqs: [
            { question: "Is FloGPT a good Bland alternative?", answer: "Yes, especially if you need more than phone calls. Bland focuses on AI calling at scale; FloGPT adds a website chat widget, a unified inbox across seven channels, a managed knowledge base, and ticketing — so phone is one part of a complete customer platform rather than the whole thing." },
            { question: "Does FloGPT support high-volume outbound calling like Bland?", answer: "Yes. FloGPT runs outbound campaigns through its trigger engine — webhooks, Shopify events, scheduled CSV lists, and time-relative cadences — with per-region quiet hours, deduplication, retry policy, and DLT-compliant routing for India built in." },
            { question: "What does FloGPT add beyond AI phone calls?", answer: "An embeddable chat widget, email/SMS/WhatsApp/Instagram/Facebook in one inbox, cross-channel contact identity, a knowledge base with citations, AI-to-human ticket handover with SLA, and analytics dashboards — the operational layer around the conversations." },
            { question: "Can I keep my phone numbers when moving from Bland?", answer: "Yes. You bring your own numbers (Twilio, Jio, MSG91) or port existing ones, then rebuild the call logic in FloGPT's visual Call Pipeline editor. Most teams recreate a working flow in a day." },
        ],
        seo: {
            title: "FloGPT vs Bland — AI Calling & Omnichannel Comparison",
            description: "FloGPT vs Bland compared: Bland is high-volume AI phone calling; FloGPT pairs voice with chat, a seven-channel CRM inbox, and a knowledge base. See the full comparison.",
            keywords: ["FloGPT vs Bland", "Bland alternative", "Bland.ai alternative", "AI phone calling platform", "Bland competitor", "AI calling alternative"],
        },
    },
    {
        slug: "flogpt-vs-retell",
        competitor: "Retell",
        competitorDomain: "retellai.com",
        category: "voice",
        Icon: CpuChipIcon,
        accent: "from-violet-500 to-indigo-500",
        softAccent: "bg-violet-500/15",
        tagline: "Voice agent builder vs a complete chat, voice & CRM platform.",
        heroSummary:
            "Retell is a voice AI platform for building, testing, and deploying conversational phone agents. FloGPT delivers voice agents as well, but bundles a website chat widget, a unified CRM inbox, a managed knowledge base, and an outbound trigger engine, so you run all of customer communication from one place — not just the call.",
        competitorSummary:
            "Retell focuses on the voice-agent lifecycle — designing conversation flows, testing them, and deploying low-latency phone agents. It suits teams whose core need is a polished voice agent they'll integrate into an existing toolset.",
        rows: voiceComparisonRows({
            voiceAgents: { tone: "yes", text: "Core product (voice agents)" },
            chatWidget: { tone: "no", text: "Voice-focused" },
            crmInbox: { tone: "no", text: "No built-in inbox" },
            knowledgeBase: { tone: "partial", text: "Supply context / integrate RAG" },
            identity: { tone: "no", text: "Handle externally" },
            handover: { tone: "partial", text: "Call transfer; no ticketing" },
            builders: { tone: "partial", text: "Conversation flow + API" },
            outbound: { tone: "partial", text: "Outbound supported; build triggers" },
            verticals: { tone: "no", text: "No vertical packs" },
            indic: { tone: "partial", text: "Multilingual; Indic depth varies" },
            pricing: { tone: "partial", text: "Per-minute usage pricing" },
        }),
        whyFloGPT: [
            { title: "Voice is one channel of seven", description: "Retell deploys voice agents; FloGPT runs the same intelligence on chat, voice, email, SMS, WhatsApp, Instagram, and Facebook, unified in a single inbox." },
            { title: "Knowledge base included", description: "Crawl your website and upload docs to ground every answer with citations — no separate vector store or retrieval pipeline to operate." },
            { title: "Built to operate support", description: "Ticketing, SLA, contact identity, and analytics dashboards make FloGPT a system of record, not just a conversation engine." },
        ],
        competitorBestFor:
            "Choose Retell if you specifically want a focused voice-agent builder and already have the surrounding CRM, channels, and reporting handled elsewhere.",
        migrationNote:
            "Teams move to FloGPT when a standalone voice agent needs to join the rest of their support: chat, messaging, tickets, and reporting. Numbers come with you; the conversation design rebuilds in the visual Call Pipeline.",
        faqs: [
            { question: "Is FloGPT a good Retell alternative?", answer: "Yes, if you want chat and messaging alongside voice. Retell is a focused voice-agent platform; FloGPT runs voice plus a website chat widget and a seven-channel CRM inbox, with a managed knowledge base and ticketing — so the agent is part of a full customer platform." },
            { question: "How does FloGPT's knowledge base compare to Retell?", answer: "FloGPT includes a managed knowledge base: it crawls your website, ingests PDFs and docs, embeds them, and grounds every chat and voice reply with inline citations. With voice-only tools you typically supply context per agent or integrate your own retrieval stack." },
            { question: "Can FloGPT handle the same voice quality and latency?", answer: "FloGPT uses streaming speech with sub-500ms first-response latency in most regions, barge-in handling, and warm transfer to humans — tuned for natural phone conversation while also feeding the unified inbox and analytics." },
            { question: "What about multilingual and Indian-market calling?", answer: "FloGPT supports 16 languages including Hindi and nine Indic locales, with DLT-compliant routing via Jio and MSG91 for India — useful if your callers are multilingual or India-based." },
        ],
        seo: {
            title: "FloGPT vs Retell — Voice Agent & Omnichannel Comparison",
            description: "FloGPT vs Retell AI compared: Retell builds voice agents; FloGPT adds chat, a seven-channel CRM inbox, and a managed knowledge base. See the capability comparison.",
            keywords: ["FloGPT vs Retell", "Retell alternative", "Retell AI alternative", "voice agent platform", "Retell competitor", "AI voice agent alternative"],
        },
    },
    {
        slug: "flogpt-vs-synthflow",
        competitor: "Synthflow",
        competitorDomain: "synthflow.ai",
        category: "voice",
        Icon: Squares2X2Icon,
        accent: "from-sky-500 to-blue-500",
        softAccent: "bg-sky-500/15",
        tagline: "No-code voice assistants vs an all-in-one AI customer platform.",
        heroSummary:
            "Synthflow is a no-code platform for building AI voice assistants without engineering. FloGPT is also no-code to start, but goes beyond voice: a website chat widget, a unified CRM inbox across seven channels, a managed knowledge base, vertical templates, and an outbound trigger engine in a single product.",
        competitorSummary:
            "Synthflow lets non-technical teams build and launch AI voice assistants quickly, with integrations to common tools. It's a solid pick when voice automation is the main goal and you want a no-code path to it.",
        rows: voiceComparisonRows({
            voiceAgents: { tone: "yes", text: "Core product (no-code voice)" },
            chatWidget: { tone: "partial", text: "Widget, API, SMS and WhatsApp, set up separately from voice" },
            crmInbox: { tone: "no", text: "No unified ticket inbox" },
            knowledgeBase: { tone: "partial", text: "Knowledge upload; lighter RAG" },
            identity: { tone: "no", text: "Via external CRM" },
            handover: { tone: "partial", text: "Transfer; no full ticketing" },
            builders: { tone: "yes", text: "No-code builder" },
            outbound: { tone: "partial", text: "Outbound + integrations" },
            verticals: { tone: "partial", text: "Templates, not full packs" },
            indic: { tone: "partial", text: "Multilingual; Indic depth varies" },
            pricing: { tone: "partial", text: "Enterprise contracts from $30,000/year" },
        }),
        whyFloGPT: [
            { title: "All channels, one inbox", description: "Synthflow chat agents (widget, API, SMS, WhatsApp) are set up separately from voice. FloGPT unifies chat, voice, email, SMS, WhatsApp, Instagram, and Facebook so a customer's whole history is one thread." },
            { title: "Deeper grounding", description: "FloGPT's managed knowledge base crawls and re-crawls your site, embeds documents, and cites sources inline on every answer across channels." },
            { title: "Runs a support org", description: "Tickets, SLA tracking, contact identity resolution, and three role-scoped analytics dashboards turn conversations into an operable support system." },
        ],
        competitorBestFor:
            "Choose Synthflow if you want a no-code voice assistant and your other channels and CRM are already covered by separate tools.",
        migrationNote:
            "Teams adopt FloGPT when they want chat and messaging plus a real inbox without stitching tools together. The no-code builder feels familiar; you add channels, a knowledge base, and ticketing without leaving the product.",
        faqs: [
            { question: "Is FloGPT a good Synthflow alternative?", answer: "Yes, if you want chat and a unified inbox in addition to voice. Synthflow is a no-code voice assistant builder; FloGPT is no-code too but spans voice, a website chat widget, seven messaging channels, a knowledge base, and ticketing in one platform." },
            { question: "Is FloGPT no-code like Synthflow?", answer: "Yes. You can launch without engineers: crawl your website to build the knowledge base, choose a vertical template, and design call and chat flows in visual editors. Developers can extend it with HTTP nodes, custom tools, and an API when needed." },
            { question: "What does FloGPT add when chat is set up separately from voice?", answer: "Synthflow chat agents run on a widget, API, SMS and WhatsApp, separate from the voice assistant, and there is no unified ticket inbox. FloGPT keeps chat and voice in one inbox, with a managed knowledge base, ticket handover, and vertical packs." },
            { question: "Does FloGPT work for non-English and Indian markets?", answer: "Yes. FloGPT supports 16 languages including Hindi and nine Indic locales, detects the caller's language automatically, and routes India traffic through DLT-compliant Jio and MSG91 providers." },
        ],
        seo: {
            title: "FloGPT vs Synthflow — No-Code AI Voice & Chat Comparison",
            description: "FloGPT vs Synthflow compared: Synthflow builds no-code voice assistants; FloGPT adds chat, a seven-channel CRM inbox, and a knowledge base. See the comparison.",
            keywords: ["FloGPT vs Synthflow", "Synthflow alternative", "Synthflow alternatives", "no-code AI voice", "Synthflow competitor", "AI voice assistant alternative"],
        },
    },
];

export const COMPARISONS_BY_SLUG: Record<string, ComparisonItem> = COMPARISONS.reduce(
    (acc, item) => {
        acc[item.slug] = item;
        return acc;
    },
    {} as Record<string, ComparisonItem>,
);

export const COMPARISON_SLUGS: string[] = COMPARISONS.map((c) => c.slug);

export const getComparisonsByCategory = (key: ComparisonCategoryKey): ComparisonItem[] =>
    COMPARISONS.filter((c) => c.category === key);

export const getRelatedComparisons = (current: ComparisonItem, limit = 3): ComparisonItem[] =>
    COMPARISONS.filter((c) => c.slug !== current.slug).slice(0, limit);
